import { IconComponent } from "@nova/component-icons"
import { ID, AddOns, BillingCycle, Licenses, SubscriptionsType } from "@nova/constants"
import { capitalize } from "nodash"
import * as Icons from "@nova/component-icons"
import { ModalNames } from "features/modals/types"
import { fileUploadUsage, flightExceededUsage } from "panels/content/side-panel/upload-flow/module"
import { License } from "@nova/schemas"

//In GB
export const LicenseCapacities = {
    [Licenses.Lite]: 2,
    [Licenses.Essentials]: 100,
    [Licenses.Pro]: 0,
}

export const LicenseCapacitiesInBytes: Record<Licenses, number> = {
    [Licenses.Lite]: 2147483648,
    [Licenses.Essentials]: 107374182400,
    [Licenses.Pro]: Number.POSITIVE_INFINITY,
}

export type LicenseDefinition = {
    type: SubscriptionsType
    title: string
    subtitle: string
    paywallMessage?: string
    pricePerMonth: number
    pricePerYear: number
    perks: string[]
    checkoutIcon: IconComponent
    icon: IconComponent
    hasMonthlyPaywall?: boolean
    contactSalesReason?: string
}

export const LicenseDefinitions: Record<Licenses, LicenseDefinition> = {
    [Licenses.Lite]: {
        type: Licenses.Lite,
        title: "Lite",
        subtitle: "The basics.",
        pricePerMonth: 0,
        pricePerYear: 0,
        perks: ["2 GB of data storage", "Export points, lines, and polygons"],
        icon: Icons.License,
        checkoutIcon: Icons.License,
    },
    [Licenses.Essentials]: {
        type: Licenses.Essentials,
        title: "Essentials",
        subtitle: "Great for getting started.",
        pricePerMonth: 60,
        pricePerYear: 540,
        perks: ["100 GB of data storage", "Export everything"],
        icon: Icons.CheckoutEssentials,
        checkoutIcon: Icons.CheckoutEssentials,
    },
    [Licenses.Pro]: {
        type: Licenses.Pro,
        title: "Pro",
        subtitle: "For professionals.",
        pricePerMonth: 125,
        pricePerYear: 1140,
        perks: ["Unlimited data storage", "Export everything", "Share direct layer downloads"],
        icon: Icons.CheckoutPro,
        checkoutIcon: Icons.CheckoutPro,
    },
}

export const AddOnDefinitions: Record<
    AddOns,
    LicenseDefinition & { sampleDataModalName?: ModalNames; sampleDataButtonTitle?: string }
> = {
    [AddOns.Colorfinder]: {
        type: AddOns.Colorfinder,
        title: "Colorfinder",
        subtitle: "Find color in difficult environments",
        paywallMessage: "Purchase a Colorfinder Add-on to identify color anomalies.",
        pricePerMonth: 32,
        pricePerYear: 240,
        perks: [
            "Supports images and videos",
            "Isolate areas of interest",
            "No upload required",
            "Search for color anomalies",
            "Accurate geospatial location",
        ],
        icon: Icons.ColorfinderAddOn,
        checkoutIcon: Icons.ColorfinderAddOn,
        contactSalesReason: "for manned aircraft integration",
        sampleDataModalName: ModalNames.TryColorfinderModal,
    },
    [AddOns.Hotspots]: {
        type: AddOns.Hotspots,
        title: "Hotspots",
        subtitle: "Automated heat detection",
        paywallMessage: "Purchase a Hotspots Add-on for automated heat detection.",
        pricePerMonth: 1525,
        pricePerYear: 2499,
        perks: [
            "AI-powered hotspot detection",
            "Visualize heat in 2D and 3D layers",
            "Complete geo-referenced output",
            "Compatible with any sensor and drone",
        ],
        icon: Icons.HotspotAddOn,
        checkoutIcon: Icons.CheckoutHotspots,
        hasMonthlyPaywall: false,
        contactSalesReason: "for manned aircraft integration",
        sampleDataModalName: ModalNames.TryHotspotsModal,
    },
    [AddOns.LiveVideo]: {
        type: AddOns.LiveVideo,
        title: "Live Stream",
        subtitle: "Live stream DJI footage to Nova",
        paywallMessage: "Upgrade to Live Stream to unlock drone livestreaming.",
        pricePerMonth: 475,
        pricePerYear: 1200,
        perks: [
            "Real-time telemetry data",
            "Geo-referenced flight view",
            "Shareable link for stream access",
            "Automatic video recording and playback",
            "Share with preset email lists",
        ],
        icon: Icons.LiveVideoAddOn,
        checkoutIcon: Icons.CheckoutVideo,
        // sampleDataModalName: ModalNames.TryLiveVideoModal,
        // sampleDataButtonTitle: "Try for free",
    },
}

export const sortLicensesInTable = (
    licenses: License[],
    primarySortKey = "fullName",
    sortDirection: "asc" | "desc" = "asc"
) => {
    const licensesWithFullName = licenses?.map((license) => ({
        ...license,
        fullName: license.assigneeId
            ? license.assigneeAttributes?.fullName || license.assigneeId
            : undefined,
    }))

    return [...(licensesWithFullName ?? [])].sort((a, b) => {
        const keys = [primarySortKey, "fullName"]
        const dirs = [sortDirection, "asc"]
        for (let i = 0; i < keys.length; i++) {
            const dir = dirs[i] === "desc" ? -1 : 1
            const aVal = (a as Record<string, unknown>)[keys[i]]
            const bVal = (b as Record<string, unknown>)[keys[i]]
            if (aVal == null && bVal == null) continue
            if (aVal == null) return 1
            if (bVal == null) return -1
            if (aVal < bVal) return -1 * dir
            if (aVal > bVal) return dir
        }
        return 0
    }) as License[]
}

export const formatSubscriptionType = (type: SubscriptionsType, caps?: boolean) => {
    if (type === AddOns.LiveVideo) {
        if (caps) return "Live Stream"
        return "live stream"
    }
    if (caps) return capitalize(type)
    return type
}

export const createStripeCheckoutSession = async (context: {
    email: string
    userId: string
    fullName: string
    redirectUrl: string
    billingCycle: BillingCycle
    quantity: number
    subscriptionType: SubscriptionsType
    liteLicenseId?: ID
}) => {
    const {
        email,
        userId,
        fullName,
        redirectUrl,
        quantity,
        subscriptionType,
        billingCycle,
        liteLicenseId,
    } = context

    const payload = { [subscriptionType]: quantity }
    const res = await fetch(
        `/api/stripe/stripe-create-checkout?email=${encodeURIComponent(
            email
        )}&fullName=${fullName}&redirectUrl=${redirectUrl}&userId=${userId}&billingCycle=${billingCycle}&assignToSelf=${true}&liteLicenseId=${liteLicenseId}`,
        {
            method: "POST",
            body: JSON.stringify(payload),
        }
    ).then((r) => r.json())

    if (!res.url) return false
    window.location.href = res.url
    return true
}

export const addExceededUsage = (flightId: ID, usage: number) => {
    if (!flightExceededUsage[flightId]) {
        flightExceededUsage[flightId] = usage
        return
    }
    flightExceededUsage[flightId] = flightExceededUsage[flightId] + usage
}
export const removeExceededUsage = (flightId: ID, usage: number) => {
    if (!flightExceededUsage[flightId]) return
    if (flightExceededUsage[flightId] - usage <= 0) {
        flightExceededUsage[flightId] = 0
        return
    }
    flightExceededUsage[flightId] = flightExceededUsage[flightId] - usage
}

export const addFlightUsage = (flightId: ID, usage: number) => {
    if (!fileUploadUsage[flightId]) {
        fileUploadUsage[flightId] = usage
        return
    }
    fileUploadUsage[flightId] = fileUploadUsage[flightId] + usage
}
export const removeFlightUsage = (flightId: ID, usage: number) => {
    if (!fileUploadUsage[flightId]) return
    if (fileUploadUsage[flightId] - usage <= 0) {
        fileUploadUsage[flightId] = 0
        return
    }
    fileUploadUsage[flightId] = fileUploadUsage[flightId] - usage
}

export const removeLocalUsage = (flightId: ID, usage: number) => {
    removeFlightUsage(flightId, usage)
    removeExceededUsage(flightId, usage)
}

export const clearExceedUsage = () => {
    for (const key in flightExceededUsage) {
        if (flightExceededUsage.hasOwnProperty(key)) {
            delete flightExceededUsage[key]
        }
    }
}

export const formatSubscriptionTitle = (
    isAddOn: boolean,
    plural: boolean,
    capitalize = true
): string => {
    let title = isAddOn ? "add-on" : "license"

    if (plural) title += "s"
    if (capitalize) title = title[0].toUpperCase() + title.slice(1)

    return title
}
